import React from 'react';
import { User, Mail, Phone, Shield, Calendar, Clock, Edit, AlertCircle } from 'lucide-react';
import Button from '../../components/ui/Button';

const UserDetails = ({ user, onClose, onEdit }) => {
    const formatDate = (dateString) => {
        if (!dateString) return 'No disponible';

        const date = new Date(dateString);
        return date.toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getRoleInfo = (roleName) => {
        const roles = {
            'administrador': {
                color: '#dc3545',
                description: 'Acceso completo al sistema, puede gestionar usuarios y configuración.'
            },
            'gerente': {
                color: '#fd7e14',
                description: 'Gestión operativa, reportes avanzados y supervisión del personal.'
            },
            'cajero': {
                color: '#28a745',
                description: 'Operaciones de venta y atención al cliente.'
            },
            'almacenista': {
                color: '#17a2b8',
                description: 'Gestión de inventario, productos y proveedores.'
            }
        };

        return roles[roleName?.toLowerCase()] || {
            color: '#6c757d',
            description: 'Rol personalizado del sistema.'
        };
    };

    const roleInfo = getRoleInfo(user.rol_nombre);

    return (
        <div className="user-details">
            {/* Header del usuario */}
            <div className="user-details-header">
                <div className="user-avatar-large">
                    {user.nombre.charAt(0).toUpperCase()}
                </div>
                <div className="user-main-info">
                    <h2 className="user-name">{user.nombre}</h2>
                    <p className="user-username">@{user.usuario}</p>
                    <div className="user-status">
                        <span className={`status-indicator ${user.activo ? 'active' : 'inactive'}`}>
                            {user.activo ? 'Activo' : 'Inactivo'}
                        </span>
                    </div>
                </div>
                <div className="header-actions">
                    <Button
                        onClick={onEdit}
                        size="small"
                        className="edit-btn"
                    >
                        <Edit size={16} />
                        Editar
                    </Button>
                </div>
            </div>

            {/* Información detallada */}
            <div className="user-details-content">
                {/* Información de contacto */}
                <div className="details-section">
                    <h3 className="section-title">
                        <Mail size={20} />
                        Información de Contacto
                    </h3>
                    <div className="details-grid">
                        <div className="detail-item">
                            <label className="detail-label">Correo Electrónico</label>
                            <div className="detail-value">
                                <Mail size={16} />
                                <span>{user.email}</span>
                            </div>
                        </div>

                        <div className="detail-item">
                            <label className="detail-label">Teléfono</label>
                            <div className="detail-value">
                                <Phone size={16} />
                                <span>{user.telefono || 'No especificado'}</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Información del rol */}
                <div className="details-section">
                    <h3 className="section-title">
                        <Shield size={20} />
                        Rol y Permisos
                    </h3>
                    <div className="role-details">
                        <div className="role-badge-large" style={{ backgroundColor: roleInfo.color }}>
                            <Shield size={20} />
                            {user.rol_nombre || 'Sin rol asignado'}
                        </div>
                        <p className="role-description">
                            {roleInfo.description}
                        </p>

                        {/* Lista de permisos (si están disponibles) */}
                        {user.permisos && user.permisos.length > 0 && (
                            <div className="permissions-list">
                                <h4 className="permissions-title">Permisos:</h4>
                                <div className="permissions-grid">
                                    {user.permisos.map((permiso, index) => (
                                        <span key={index} className="permission-tag">
                                            {permiso.replace('_', ' ').toUpperCase()}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Información del sistema */}
                <div className="details-section">
                    <h3 className="section-title">
                        <Clock size={20} />
                        Información del Sistema
                    </h3>
                    <div className="details-grid">
                        <div className="detail-item">
                            <label className="detail-label">Fecha de Registro</label>
                            <div className="detail-value">
                                <Calendar size={16} />
                                <span>{formatDate(user.fecha_creacion)}</span>
                            </div>
                        </div>

                        <div className="detail-item">
                            <label className="detail-label">Último Acceso</label>
                            <div className="detail-value">
                                <Clock size={16} />
                                <span>{formatDate(user.ultimo_acceso)}</span>
                            </div>
                        </div>

                        <div className="detail-item">
                            <label className="detail-label">ID de Usuario</label>
                            <div className="detail-value">
                                <User size={16} />
                                <span>#{user.id}</span>
                            </div>
                        </div>

                        <div className="detail-item">
                            <label className="detail-label">Estado de la Cuenta</label>
                            <div className="detail-value">
                                {user.activo ? (
                                    <>
                                        <div className="status-icon active"></div>
                                        <span>Cuenta activa y operativa</span>
                                    </>
                                ) : (
                                    <>
                                        <AlertCircle size={16} className="status-icon inactive" />
                                        <span>Cuenta desactivada</span>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Estadísticas de actividad (si están disponibles) */}
                {user.estadisticas && (
                    <div className="details-section">
                        <h3 className="section-title">
                            <User size={20} />
                            Estadísticas de Actividad
                        </h3>
                        <div className="stats-grid">
                            <div className="stat-item">
                                <span className="stat-value">{user.estadisticas.total_ventas || 0}</span>
                                <span className="stat-label">Ventas Realizadas</span>
                            </div>
                            <div className="stat-item">
                                <span className="stat-value">{user.estadisticas.sesiones || 0}</span>
                                <span className="stat-label">Sesiones Iniciadas</span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Notas adicionales */}
                {!user.activo && (
                    <div className="details-section warning">
                        <div className="warning-content">
                            <AlertCircle size={20} />
                            <div>
                                <h4>Usuario Inactivo</h4>
                                <p>
                                    Este usuario está desactivado y no puede acceder al sistema.
                                    Para reactivarlo, edita el usuario y cambia su estado a "Activo".
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Acciones del pie */}
            <div className="user-details-footer">
                <Button
                    onClick={onClose}
                    variant="secondary"
                >
                    Cerrar
                </Button>

                <Button
                    onClick={onEdit}
                    className="primary-btn"
                >
                    <Edit size={16} />
                    Editar Usuario
                </Button>
            </div>
        </div>
    );
};

export default UserDetails;